import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Loader2, ChevronLeft, ChevronRight, ArrowUp, ArrowDown, ArrowUpDown, Download } from 'lucide-react';
import PoLineLink from '../../common/PoLineLink';
import PoHeaderLink from '../../common/PoHeaderLink';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../ui/dialog';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { toast } from 'sonner';
import { purchasingAPI, qualityAPI } from '../../../lib/api';
import type { POLineSchedule, NonConformanceReport } from '../../../lib/api';
import apiClient from '../../../lib/api/client';
import { useFormatters } from '../../../contexts/PreferencesContext';
import { useQualityMeasureLabel } from '../../../lib/qualityLabels';
import { isoMinusDays } from './currentWeekDrilldownParams';
import { OtifStatusPill } from './CurrentWeekDrilldownTable';
import ManualAgreementBadge from '../../purchasing/ManualAgreementBadge';
import OtifOverrideDialog from '../../purchasing/OtifOverrideDialog';
import { useAuth } from '../../../contexts/AuthContext';
import { SUPPLIER_PROMISE_DATE_SHORT } from '../../../config/dateLabels';

/** Local copy of the dashboard filter scope — mirrors the inline shape in
 *  `shared.tsx`'s ``PeriodContentProps.filters``. Defined here so the modal
 *  doesn't depend on shared.tsx exporting a named type (the export name
 *  varies across in-flight refactors). */
export interface DashboardFilterScope {
  customer_id?: number | string;
  supplier_id?: number | string;
  part_number?: string;
  erp_order_type: '1' | '4';
}

/**
 * YTD drilldown modes — one per clickable tile / chart segment.
 *
 * Schedule-backed modes hit /purchasing/schedules/ and stream through
 * /purchasing/schedules/export-csv/ for Excel/CSV. NCR modes hit
 * /quality/ncrs/. Appeals hits /purchasing/delivery-appeals/.
 */
export type YTDDrilldownMode =
  | 'quality_ppm'
  | 'quality_ncrs'
  | 'delivery_otif'
  | 'delivery_otd'
  | 'delivery_appeals'
  | 'cost_open'
  | 'cost_received'
  | 'cost_arrears';

export type YTDDrilldownDataset = 'schedules' | 'ncrs' | 'appeals';

const MODE_DATASET: Record<YTDDrilldownMode, YTDDrilldownDataset> = {
  quality_ppm: 'ncrs',
  quality_ncrs: 'ncrs',
  delivery_otif: 'schedules',
  delivery_otd: 'schedules',
  delivery_appeals: 'appeals',
  cost_open: 'schedules',
  cost_received: 'schedules',
  cost_arrears: 'schedules',
};

const PAGE_SIZE = 50;

interface YTDDrilldownModalProps {
  open: boolean;
  onClose: () => void;
  mode: YTDDrilldownMode;
  /** Full dashboard filter scope from the shell — customer / supplier / part / erp_order_type. */
  filters: DashboardFilterScope;
  /** Period start (YTD => Jan 1; per-month => month start). ISO date YYYY-MM-DD. */
  periodStart: string;
  /** Period end (YTD => today; per-month => month end). ISO date YYYY-MM-DD. */
  periodEnd: string;
  /** Today as ISO — used by the Arrears mode (past-due cutoff). */
  todayIso: string;
  /** Optional period label appended to the modal title (e.g. "Mar 2026"). */
  periodLabel?: string;
  /** Arrears grace buffer (days) — arrears cutoff = today - buffer. */
  arrearsBufferDays?: number;
}

interface ModeMeta {
  title: string;
  description: string;
  /** Default ordering string for the listing. */
  defaultOrdering: string;
}

function buildModeMeta(qualityLabel: string): Record<YTDDrilldownMode, ModeMeta> {
  return {
  quality_ppm: { title: `Quality — ${qualityLabel} rejects`, description: 'NCRs raised in period', defaultOrdering: '-ncr_date' },
  quality_ncrs: { title: 'Quality — NCRs', description: 'NCRs raised in period', defaultOrdering: '-ncr_date' },
  delivery_otif: { title: 'Delivery — OTIF schedules', description: 'Schedule lines due in period (OTIF view)', defaultOrdering: 'schedule_date' },
  delivery_otd:  { title: 'Delivery — OTD schedules',  description: 'Schedule lines due in period (OTD view)',  defaultOrdering: 'schedule_date' },
  delivery_appeals: { title: 'Delivery — Appeals', description: 'Delivery appeals in scope', defaultOrdering: '-created_at' },
  cost_open:     { title: 'Cost — Open schedule lines', description: 'Outstanding schedule lines due in period', defaultOrdering: 'schedule_date' },
  cost_received: { title: 'Cost — Received schedules',  description: 'Schedule lines partially or fully shipped in period', defaultOrdering: 'schedule_date' },
  cost_arrears:  { title: 'Cost — Arrears',             description: 'Past-due open schedule lines',  defaultOrdering: 'schedule_date' },
  };
}

// ---- Schedule columns (used for OTIF/OTD/Open/Received/Arrears + chart bar drilldowns) ----

const SCHEDULE_SORT_FIELDS: Record<string, string | null> = {
  schedule_date: 'schedule_date',
  received_date: null,
  supplier_promise_date: 'supplier_promise_date',
  po_number: 'po_line__po__po_number',
  line_number: 'po_line__line_number',
  schedule_line_number: 'schedule_line_number_int',
  part_number: 'po_line__part_number',
  description: null,
  part_description: null,
  supplier_name: 'po_line__po__supplier__name',
  schedule_qty: 'schedule_qty',
  shipped_qty: 'shipped_qty',
  balance_qty: 'balance_qty',
  otif_status: null,
};

interface ScheduleColumnDef { id: keyof typeof SCHEDULE_SORT_FIELDS; label: string; align?: 'left' | 'right'; }

const SCHEDULE_COLUMNS: ScheduleColumnDef[] = [
  { id: 'schedule_date', label: 'Schedule Date', align: 'left' },
  { id: 'received_date', label: 'Received Date', align: 'left' },
  { id: 'supplier_promise_date', label: SUPPLIER_PROMISE_DATE_SHORT, align: 'left' },
  { id: 'po_number', label: 'PO #', align: 'left' },
  { id: 'line_number', label: 'Line', align: 'right' },
  { id: 'schedule_line_number', label: 'Sched #', align: 'right' },
  { id: 'part_number', label: 'Part', align: 'left' },
  { id: 'part_description', label: 'Part Description', align: 'left' },
  { id: 'supplier_name', label: 'Supplier', align: 'left' },
  { id: 'schedule_qty', label: 'Sched Qty', align: 'right' },
  { id: 'shipped_qty', label: 'Received Qty', align: 'right' },
  { id: 'balance_qty', label: 'Open Qty', align: 'right' },
  // Matches the weekly drilldown: the OTIF verdict, not the shipping state.
  { id: 'otif_status', label: 'OTIF Status', align: 'left' },
];

// ---- NCR columns ----

const NCR_SORT_FIELDS: Record<string, string | null> = {
  ncr_date: 'ncr_date',
  ncr_number: 'ncr_number',
  part_number: 'part_number',
  supplier_name: 'supplier__name',
  reject_qty: null,
  severity: 'severity',
  status: 'status',
  raised_by_name: null,
};

interface NCRColumnDef { id: keyof typeof NCR_SORT_FIELDS; label: string; align?: 'left' | 'right'; }

const NCR_COLUMNS: NCRColumnDef[] = [
  { id: 'ncr_date', label: 'NCR Date', align: 'left' },
  { id: 'ncr_number', label: 'NCR #', align: 'left' },
  { id: 'part_number', label: 'Part', align: 'left' },
  { id: 'supplier_name', label: 'Supplier', align: 'left' },
  { id: 'reject_qty', label: 'Reject Qty', align: 'right' },
  { id: 'severity', label: 'Severity', align: 'left' },
  { id: 'status', label: 'Status', align: 'left' },
  { id: 'raised_by_name', label: 'Raised By', align: 'left' },
];

// ---- Appeal columns ----

interface DeliveryAppealRow {
  id: number;
  appeal_sequence?: number;
  status?: string;
  is_customer_appeal?: boolean;
  created_at?: string;
  reason?: string;
  // GRN-derived display fields the list serializer typically returns. We
  // read them defensively because the canonical type isn't exported.
  grn_number?: string;
  po_number?: string;
  part_number?: string;
  supplier_name?: string;
}

const APPEAL_SORT_FIELDS: Record<string, string | null> = {
  created_at: 'created_at',
  status: 'status',
  appeal_sequence: 'appeal_sequence',
  grn_number: null,
  po_number: null,
  part_number: null,
  supplier_name: null,
};

interface AppealColumnDef { id: keyof typeof APPEAL_SORT_FIELDS; label: string; align?: 'left' | 'right'; }

const APPEAL_COLUMNS: AppealColumnDef[] = [
  { id: 'created_at', label: 'Created', align: 'left' },
  { id: 'appeal_sequence', label: 'Seq', align: 'right' },
  { id: 'grn_number', label: 'GRN', align: 'left' },
  { id: 'po_number', label: 'PO #', align: 'left' },
  { id: 'part_number', label: 'Part', align: 'left' },
  { id: 'supplier_name', label: 'Supplier', align: 'left' },
  { id: 'status', label: 'Status', align: 'left' },
];

const SEVERITY_BADGE: Record<string, string> = {
  critical: 'bg-red-50 text-red-700 border-red-200',
  major: 'bg-amber-50 text-amber-700 border-amber-200',
  minor: 'bg-slate-50 text-slate-700 border-slate-200',
};

function buildPageList(current: number, total: number): Array<number | '…'> {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const out: Array<number | '…'> = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  if (start > 2) out.push('…');
  for (let p = start; p <= end; p++) out.push(p);
  if (end < total - 1) out.push('…');
  out.push(total);
  return out;
}

/** Build the raw param map shared by list AND export — keeps tile/list/CSV in lockstep. */
/** OTIF status sub-tabs for the YTD OTIF drilldown ('' = All denominator). */
export interface ScheduleStatusTab { label: string; value: string; }
function getScheduleStatusTabs(mode: YTDDrilldownMode): ScheduleStatusTab[] | null {
  if (mode === 'delivery_otif') {
    return [
      { label: 'All', value: '' },
      { label: 'Passed', value: 'passed' },
      { label: 'Pending', value: 'pending' },
      { label: 'Failed', value: 'failed' },
    ];
  }
  return null;
}

function buildScheduleParams(
  mode: YTDDrilldownMode,
  filters: DashboardFilterScope,
  periodStart: string,
  periodEnd: string,
  todayIso: string,
  otifStatus = '',
  arrearsBufferDays = 0,
): Record<string, string | number | boolean> {
  const params: Record<string, string | number | boolean> = {
    exclude_line_zero: true,
  };
  if (otifStatus) params['otif_status'] = otifStatus;
  if (filters.customer_id) params['po_line__po__customer'] = filters.customer_id;
  if (filters.supplier_id) params['po_line__po__supplier'] = filters.supplier_id;
  if (filters.part_number) params['po_line__part_number'] = filters.part_number;
  if (filters.erp_order_type) params['po_line__po__erp_order_type'] = filters.erp_order_type;

  // Period scope — Arrears uses past-due cutoff; everything else uses
  // schedule_date BETWEEN periodStart AND periodEnd. The OTIF/OTD/Open/
  // Received tiles all share the YTD bucket SQL's window predicate.
  if (mode === 'cost_arrears') {
    // Match tile SQL: schedule_date < today AND balance_qty > 0 within
    // the YTD window. The dashboard's is_past_due predicate is strict
    // `<` (today is "due today", not past-due) — modal must mirror.
    params['schedule_date__gte'] = periodStart;
    params['schedule_date__lt'] = isoMinusDays(todayIso, arrearsBufferDays);
    params['balance_qty__gt'] = 0;
  } else {
    params['schedule_date__gte'] = periodStart;
    params['schedule_date__lte'] = periodEnd;
    if (mode === 'cost_open') {
      params['outstanding'] = true;
    } else if (mode === 'cost_received') {
      // Tile counts schedules with shipped_qty > 0 in the period.
      // Backend filterset now exposes shipped_qty__gt directly so the
      // modal count matches the tile rather than proxying via status.
      params['shipped_qty__gt'] = 0;
    }
  }
  return params;
}

function buildNCRParams(
  filters: DashboardFilterScope,
  periodStart: string,
  periodEnd: string,
  mode?: YTDDrilldownMode,
): Record<string, string | number | boolean> {
  // Mirrors the analytics quality SQL: scope by company (customer) +
  // supplier + ncr_date in window. Backend filterset accepts these
  // exact field names.
  const params: Record<string, string | number | boolean> = {
    ncr_date__gte: periodStart,
    ncr_date__lte: periodEnd,
  };
  // CR-7173: `customer` (not `company`) — the NCR filterset role-routes
  // `company`, treating it as a supplier-site id for supplier users, which
  // zeroed the supplier's drill-down. `customer` always scopes NCR.company.
  if (filters.customer_id) params['customer'] = filters.customer_id;
  if (filters.supplier_id) params['supplier'] = filters.supplier_id;
  if (filters.part_number) params['part_number__icontains'] = filters.part_number;
  // Scope through the PO join so the modal matches the tile's scoped_ncrs
  // CTE (which excludes NCRs without a po_line or linked to a different
  // order type).
  if (filters.erp_order_type) params['po_line__po__erp_order_type'] = filters.erp_order_type;
  // PPM tile drills only into NCRs with rejected parts so the row set
  // differs from the NCRs tile (which drills to every NCR raised).
  if (mode === 'quality_ppm') params['reject_qty__gt'] = 0;
  return params;
}

function buildAppealParams(
  filters: DashboardFilterScope,
  periodStart?: string,
  periodEnd?: string,
): Record<string, string | number | boolean> {
  // Backend filterset now accepts created_at + customer + erp_order_type
  // lookups so the appeals drilldown count lines up with the YTD Appeals
  // tile (which counts DeliveryAppeal.created_at::date in the period).
  const params: Record<string, string | number | boolean> = {};
  if (filters.customer_id) params['grn__po_line__po__customer'] = filters.customer_id;
  if (filters.supplier_id) params['grn__po_line__po__supplier'] = filters.supplier_id;
  if (filters.part_number) params['grn__po_line__part_number__icontains'] = filters.part_number;
  if (filters.erp_order_type) params['grn__po_line__po__erp_order_type'] = filters.erp_order_type;
  if (periodStart) params['created_at__date__gte'] = periodStart;
  if (periodEnd) params['created_at__date__lte'] = periodEnd;
  return params;
}

export function YTDDrilldownModal({
  open, onClose, mode, filters, periodStart, periodEnd, todayIso, periodLabel,
  arrearsBufferDays = 0,
}: YTDDrilldownModalProps) {
  const { formatDate, formatNumber } = useFormatters();
  const qualityLabel = useQualityMeasureLabel();
  const dataset = MODE_DATASET[mode];
  const meta = useMemo(() => buildModeMeta(qualityLabel)[mode], [qualityLabel, mode]);

  const [schedules, setSchedules] = useState<POLineSchedule[]>([]);
  const [ncrs, setNcrs] = useState<NonConformanceReport[]>([]);
  const [appeals, setAppeals] = useState<DeliveryAppealRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageInput, setPageInput] = useState<string>('1');
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [sortCol, setSortCol] = useState<string>('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const statusTabs = getScheduleStatusTabs(mode);
  const [otifStatus, setOtifStatus] = useState('');

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const sortFields = dataset === 'schedules'
    ? SCHEDULE_SORT_FIELDS
    : dataset === 'ncrs'
      ? NCR_SORT_FIELDS
      : APPEAL_SORT_FIELDS;
  const { user } = useAuth();
  // Same population that operates GRN Repair; the backend enforces it too.
  const canOtifOverride = !!user?.is_superuser || !!user?.is_staff;
  const [overrideTarget, setOverrideTarget] = useState<POLineSchedule | null>(null);
  // Busts the 60s Cache-Control on the schedules list after an override, so
  // the row comes back with its new verdict instead of the stale one.
  const cacheBustRef = useRef<number | null>(null);
  const showOverrideCol = dataset === 'schedules' && canOtifOverride;

  const columns = dataset === 'schedules'
    ? SCHEDULE_COLUMNS
    : dataset === 'ncrs'
      ? NCR_COLUMNS
      : APPEAL_COLUMNS;

  const orderingParam = useMemo(() => {
    if (!sortCol) return meta.defaultOrdering;
    const field = sortFields[sortCol];
    if (!field) return meta.defaultOrdering;
    return sortDir === 'desc' ? `-${field}` : field;
  }, [sortCol, sortDir, sortFields, meta.defaultOrdering]);

  const fetchPage = useCallback(async (targetPage: number) => {
    setLoading(true);
    setError(null);
    try {
      if (dataset === 'schedules') {
        const params = {
          ...buildScheduleParams(mode, filters, periodStart, periodEnd, todayIso, otifStatus, arrearsBufferDays),
          ...(cacheBustRef.current ? { _t: cacheBustRef.current } : {}),
          page: targetPage,
          page_size: PAGE_SIZE,
          ordering: orderingParam,
        };
        const res = await purchasingAPI.getSchedules({ ...params, all_suppliers: true });
        setSchedules(res.data.results ?? []);
        setTotal(res.data.count ?? 0);
      } else if (dataset === 'ncrs') {
        const params = {
          ...buildNCRParams(filters, periodStart, periodEnd, mode),
          page: targetPage,
          page_size: PAGE_SIZE,
          ordering: orderingParam,
        };
        const res = await qualityAPI.getNCRs(params);
        setNcrs(res.data.results ?? []);
        setTotal(res.data.count ?? 0);
      } else {
        const params = {
          ...buildAppealParams(filters, periodStart, periodEnd),
          page: targetPage,
          page_size: PAGE_SIZE,
          ordering: orderingParam,
        };
        const res = await apiClient.get<{ count: number; results: DeliveryAppealRow[] }>(
          '/purchasing/delivery-appeals/',
          { params },
        );
        setAppeals(res.data.results ?? []);
        setTotal(res.data.count ?? 0);
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to load drilldown data';
      setError(msg);
      setSchedules([]);
      setNcrs([]);
      setAppeals([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [dataset, mode, filters, periodStart, periodEnd, todayIso, orderingParam, otifStatus, arrearsBufferDays]);

  // Reset the OTIF status slice when the modal opens or the mode changes.
  useEffect(() => { setOtifStatus(''); }, [open, mode]);

  useEffect(() => {
    if (!open) return;
    setPage(1);
    setPageInput('1');
    setSortCol('');
    fetchPage(1);
  }, [open, mode, periodStart, periodEnd, fetchPage]);

  useEffect(() => {
    if (!open || page === 1) return;
    fetchPage(page);
  }, [page, open, fetchPage]);

  useEffect(() => { setPageInput(String(page)); }, [page]);

  const goToPage = useCallback((p: number) => {
    if (p < 1 || p > totalPages || p === page || loading) return;
    setPage(p);
  }, [page, totalPages, loading]);

  const handleHeaderClick = useCallback((colId: string) => {
    if (!sortFields[colId]) return;
    if (sortCol === colId) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortCol(colId);
      setSortDir('asc');
    }
  }, [sortCol, sortFields]);

  const sortIcon = (colId: string) => {
    if (!sortFields[colId]) return null;
    if (sortCol !== colId) return <ArrowUpDown className="h-3 w-3 text-muted-foreground inline ml-1" />;
    return sortDir === 'asc'
      ? <ArrowUp className="h-3 w-3 text-primary inline ml-1" />
      : <ArrowDown className="h-3 w-3 text-primary inline ml-1" />;
  };

  const handleExport = useCallback(async () => {
    // All three datasets stream true XLSX from the backend now (openpyxl).
    // Schedules → /purchasing/schedules/export-xlsx/
    // NCRs     → /quality/ncrs/export-xlsx/
    // Appeals  → /purchasing/delivery-appeals/export-xlsx/
    // Filters mirror the list call exactly so modal count == workbook rows.
    setExporting(true);
    try {
      let params: Record<string, string | number | boolean>;
      let url: string;
      let downloadName: string;
      const today = new Date().toISOString().slice(0, 10);
      if (dataset === 'schedules') {
        params = buildScheduleParams(mode, filters, periodStart, periodEnd, todayIso, otifStatus, arrearsBufferDays);
        url = '/purchasing/schedules/export-xlsx/';
        downloadName = `ytd_${mode}_${today}.xlsx`;
      } else if (dataset === 'ncrs') {
        params = buildNCRParams(filters, periodStart, periodEnd, mode);
        url = '/quality/ncrs/export-xlsx/';
        downloadName = `ytd_${mode}_${today}.xlsx`;
      } else {
        params = buildAppealParams(filters, periodStart, periodEnd);
        url = '/purchasing/delivery-appeals/export-xlsx/';
        downloadName = `ytd_${mode}_${today}.xlsx`;
      }
      const qs = new URLSearchParams(
        Object.entries(params)
          .filter(([, v]) => v !== undefined && v !== null && v !== '')
          .map(([k, v]) => [k, String(v)]),
      ).toString();
      const resp = await apiClient.get(`${url}?${qs}`, { responseType: 'blob' });
      const blob = new Blob([resp.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = downloadName;
      a.click();
      URL.revokeObjectURL(objectUrl);
      toast.success('Excel export ready');
    } catch (err: unknown) {
      console.error('YTD drilldown Excel export failed:', err);
      toast.error('Failed to export Excel');
    } finally {
      setExporting(false);
    }
  }, [dataset, mode, filters, periodStart, periodEnd, todayIso, otifStatus, arrearsBufferDays]);

  const pageButtons = useMemo(() => buildPageList(page, totalPages), [page, totalPages]);

  const renderTitle = () => (
    <span>
      {meta.title}
      {periodLabel ? <span className="text-muted-foreground font-normal"> — {periodLabel}</span> : null}
    </span>
  );

  const renderDescription = () => (
    <span>
      {total.toLocaleString()} record{total === 1 ? '' : 's'} · {meta.description}
      {filters.part_number ? <> · part <span className="font-mono">{filters.part_number}</span></> : null}
      {filters.supplier_id ? <> · supplier #{filters.supplier_id}</> : null}
    </span>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-6xl">
        <DialogHeader>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <DialogTitle>{renderTitle()}</DialogTitle>
              <DialogDescription>{renderDescription()}</DialogDescription>
            </div>
            <Button
              size="sm" variant="outline"
              onClick={handleExport}
              disabled={exporting || loading || total === 0}
              title="Export all rows matching this drilldown"
            >
              {exporting
                ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                : <Download className="h-4 w-4 mr-1" />}
              Export to Excel
            </Button>
          </div>
        </DialogHeader>

        {statusTabs && (
          <div className="flex flex-wrap gap-1 border-b pb-2">
            {statusTabs.map((t) => {
              const active = t.value === otifStatus;
              return (
                <button
                  key={t.value || 'all'}
                  onClick={() => { if (t.value !== otifStatus) setOtifStatus(t.value); }}
                  className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                    active ? 'bg-primary text-primary-foreground font-medium' : 'text-slate-600 hover:bg-slate-100'
                  }`}
                  aria-current={active ? 'true' : undefined}
                >
                  {t.label}
                </button>
              );
            })}
          </div>
        )}

        {error && <div className="text-sm text-red-600 py-3">{error}</div>}

        {loading && (dataset === 'schedules' ? schedules.length === 0 : dataset === 'ncrs' ? ncrs.length === 0 : appeals.length === 0) ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        ) : total === 0 ? (
          <div className="text-sm text-muted-foreground py-10 text-center">No records match this drilldown.</div>
        ) : (
          <div className="overflow-auto max-h-[60vh] border rounded-md">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 sticky top-0 z-10">
                <tr>
                  {columns.map((c) => {
                    const sortable = sortFields[c.id] !== null;
                    const align = c.align === 'right' ? 'text-right' : 'text-left';
                    const cursor = sortable ? 'cursor-pointer hover:bg-slate-100' : '';
                    return (
                      <th
                        key={c.id}
                        className={`px-3 py-2 font-medium text-slate-700 select-none ${align} ${cursor}`}
                        onClick={() => handleHeaderClick(c.id)}
                      >
                        {c.label}{sortIcon(c.id)}
                      </th>
                    );
                  })}
                  {showOverrideCol && (
                    <th className="px-3 py-2 font-medium text-slate-700 text-left whitespace-nowrap" title="Staff: manually override the OTIF verdict">
                      Override
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {dataset === 'schedules' && schedules.map((r) => (
                  <tr key={r.id} className="hover:bg-slate-50">
                    <td className="px-3 py-2 whitespace-nowrap">{r.schedule_date ? formatDate(r.schedule_date) : '—'}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{r.received_date ? formatDate(r.received_date) : '—'}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{r.supplier_promise_date ? formatDate(r.supplier_promise_date) : '—'}</td>
                    {/* CR-1154: PO number links to the PO header; line to the line item. */}
                    <td className="px-3 py-2 font-mono text-xs">
                      <PoHeaderLink poLineId={r.po_line_id} poNumber={r.po_number}>{r.po_number}</PoHeaderLink>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      <PoLineLink poLineId={r.po_line_id}>{r.line_number}</PoLineLink>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.schedule_line_number}</td>
                    <td className="px-3 py-2 font-mono text-xs">{r.part_number}</td>
                    <td className="px-3 py-2 text-xs max-w-[260px] truncate" title={r.part_description}>{r.part_description || '—'}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{r.supplier_name || '—'}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{formatNumber(parseFloat(r.schedule_qty || '0'))}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{formatNumber(parseFloat(r.shipped_qty || '0'))}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{formatNumber(parseFloat(r.balance_qty || '0'))}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1">
                        <OtifStatusPill
                          value={r.otif_status}
                          overridden={r.otif_overridden}
                          overrideReason={r.otif_override_reason}
                        />
                        <ManualAgreementBadge schedule={r} compact />
                      </div>
                    </td>
                    {showOverrideCol && (
                      <td className="px-3 py-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-6 px-2 text-xs"
                          onClick={() => setOverrideTarget(r)}
                        >
                          {r.otif_overridden ? 'Manage' : 'Override'}
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
                {dataset === 'ncrs' && ncrs.map((n) => (
                  <tr key={n.id} className="hover:bg-slate-50">
                    <td className="px-3 py-2 whitespace-nowrap">{n.ncr_date ? formatDate(n.ncr_date) : '—'}</td>
                    <td className="px-3 py-2 font-mono text-xs">
                      <a
                        href={`/quality/ncr/${n.id}/edit`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                        title="Open NCR in a new tab"
                      >
                        {n.ncr_number}
                      </a>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{n.part_number}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{n.supplier_name || '—'}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{formatNumber(n.reject_qty || 0)}</td>
                    <td className="px-3 py-2">
                      <Badge variant="outline" className={SEVERITY_BADGE[n.severity] || ''}>
                        {n.severity_display || n.severity}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 text-xs">{n.status_display || n.status}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{n.raised_by_name || '—'}</td>
                  </tr>
                ))}
                {dataset === 'appeals' && appeals.map((a) => (
                  <tr key={a.id} className="hover:bg-slate-50">
                    <td className="px-3 py-2 whitespace-nowrap">{a.created_at ? formatDate(a.created_at) : '—'}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{a.appeal_sequence ?? '—'}</td>
                    <td className="px-3 py-2 font-mono text-xs">{a.grn_number || '—'}</td>
                    <td className="px-3 py-2 font-mono text-xs">{a.po_number || '—'}</td>
                    <td className="px-3 py-2 font-mono text-xs">{a.part_number || '—'}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{a.supplier_name || '—'}</td>
                    <td className="px-3 py-2 text-xs">{a.status || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {total > PAGE_SIZE && (
          <div className="flex items-center justify-between gap-2 pt-2 flex-wrap">
            <div className="flex items-center gap-1">
              <Button
                size="sm" variant="outline"
                disabled={page <= 1 || loading}
                onClick={() => goToPage(page - 1)}
                aria-label="Previous page"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              {pageButtons.map((p, idx) => (
                p === '…' ? (
                  <span key={`gap-${idx}`} className="px-2 text-xs text-muted-foreground">…</span>
                ) : (
                  <Button
                    key={p}
                    size="sm"
                    variant={p === page ? 'default' : 'outline'}
                    disabled={loading}
                    onClick={() => goToPage(p)}
                    aria-current={p === page ? 'page' : undefined}
                    className="min-w-[2.25rem]"
                  >
                    {p}
                  </Button>
                )
              ))}
              <Button
                size="sm" variant="outline"
                disabled={page >= totalPages || loading}
                onClick={() => goToPage(page + 1)}
                aria-label="Next page"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>

            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <label htmlFor="ytd-page-jump">Go to page</label>
              <input
                id="ytd-page-jump"
                type="number"
                min={1}
                max={totalPages}
                value={pageInput}
                disabled={loading}
                onChange={(e) => setPageInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    const n = parseInt(pageInput, 10);
                    if (!Number.isNaN(n)) goToPage(Math.max(1, Math.min(totalPages, n)));
                  }
                }}
                onBlur={() => {
                  const n = parseInt(pageInput, 10);
                  if (!Number.isNaN(n)) goToPage(Math.max(1, Math.min(totalPages, n)));
                  else setPageInput(String(page));
                }}
                className="h-7 w-16 rounded-md border border-slate-200 bg-white px-2 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-300"
              />
              <span>of {totalPages.toLocaleString()}</span>
            </div>
          </div>
        )}
        {/* Staff-only manual OTIF override — the same dialog the weekly and
            12-week drilldowns use, so a verdict is changed identically from
            any tab. */}
        <OtifOverrideDialog
          open={overrideTarget !== null}
          schedule={overrideTarget}
          onClose={() => setOverrideTarget(null)}
          onDone={() => { cacheBustRef.current = Date.now(); fetchPage(page); }}
        />
      </DialogContent>
    </Dialog>
  );
}
